import { SOURCES } from './eventSources';

// ─── Naming guide ────────────────────────────────────────────────────────────
// EVENTS key   : SHORTNAME_MON_YYYY (e.g. ZOOM_JAN_2026, VALENTINES_FEB_2026)
// id           : kebab-case, NEVER change after data is written to Firestore
// source       : import from eventSources.js — never type the string manually
// theme        : key from eventThemes.js — use 'default' if no seasonal theme
// info         : displayed in EventInfoCard on both registration and feedback pages
// closesAt     : exact moment the form auto-closes, ISO 8601 WITH offset — use IST:
//                '2026-10-10T16:00:00+05:30'. Required on every registration, and on
//                feedback whenever feedback.active is true. `npm run build` fails without it.
//                A form is open only while active: true AND now < closesAt.
// ─────────────────────────────────────────────────────────────────────────────

export const EVENTS = {
  ZOOM_JAN_2026: {
    id: 'zoom-meeting-jan-2026',
    name: 'Zoom Community Meeting',
    theme: 'default',
    info: {
      date: '8th February 2026',
      format: 'Online (Zoom)',
      description: 'An evening of engaging conversations, interactive games, and lots of masti - welcoming all disabilities 🎉 (open to everyone 18+)'
    },
    registration: {
      source: SOURCES.ZOOM_JAN_2026.registration,
      closesAt: '2026-02-08T00:00:00+05:30',
      active: false,
      cap: 50
    },
    feedback: {
      source: SOURCES.ZOOM_JAN_2026.feedback,
      active: false,
      cap: 70
    }
  },
  VALENTINES_FEB_2026: {
    id: 'valentines-event-feb-2026',
    name: "Valentine's Special Event",
    theme: 'valentines',
    info: {
      date: '14 February 2026',
      format: 'Online (Zoom)',
      description: "An evening of lively chatter and games with a focus on love 💕 (open to all 18+)"
    },
    registration: {
      source: SOURCES.VALENTINES_FEB_2026.registration,
      closesAt: '2026-02-14T00:00:00+05:30',
      active: false,
      cap: 500
    },
    feedback: {
      source: SOURCES.VALENTINES_FEB_2026.feedback,
      active: false,
      cap: 70
    }
  },
  WOMENS_MAR_2026: {
    id: 'womens-special-mar-2026',
    name: "Women's Special: Love, Friendship & Safety",
    theme: 'default',
    info: {
      date: '7th March 2026, 5:00 PM',
      format: 'Online (Zoom)',
      description: "A special webinar for women on finding friends and love, and staying safe while doing so 💜 (open to all women 18+)"
    },
    registration: {
      source: SOURCES.WOMENS_MAR_2026.registration,
      closesAt: '2026-03-07T17:00:00+05:30',
      active: false,
      cap: 500
    },
    feedback: {
      source: SOURCES.WOMENS_MAR_2026.feedback,
      active: false,
      cap: 150
    },
  },
  SOCIAL_APR_2026: {
    id: 'social-sunday-apr-2026',
    name: 'Social Sunday',
    theme: 'default',
    info: {
      date: '5th April 2026, 5:00 PM',
      format: 'Online (Zoom)',
      description: 'An afternoon of fun interactions and engaging conversations - open to everyone 18+ 🎉'
    },
    registration: {
      source: SOURCES.SOCIAL_APR_2026.registration,
      closesAt: '2026-04-05T17:00:00+05:30',
      active: false,
      cap: 500
    },
    feedback: {
      source: SOURCES.SOCIAL_APR_2026.feedback,
      active: false,
      cap: 150
    }
  },
  CYBERSAFETY_AUG_2026: {
    id: 'cyber-safety-aug-2026',
    name: 'Stay Alert, Stay Safe: Cyber Frauds & Digital Financial Safety',
    theme: 'default',
    info: {
      date: '14th August 2026, 4:00 PM',
      format: 'Online (Webinar)',
      description: "An awareness session on cyber frauds and digital financial safety — covering red flags, safe banking/UPI practices, and what to do if you're targeted 🛡️ (open to everyone)"
    },
    registration: {
      source: SOURCES.CYBERSAFETY_AUG_2026.registration,
      closesAt: '2026-08-14T16:00:00+05:30',
      active: false,
      cap: 500
    },
    feedback: {
      source: SOURCES.CYBERSAFETY_AUG_2026.feedback,
      active: false,
      cap: 150
    }
  },
  NOBARRIERS_AUG_2026: {
    id: 'no-barriers-aug-2026',
    name: 'Together, No Barriers: Finding Love and Companionship for Persons with Disabilities',
    theme: 'romance',
    info: {
      date: '22nd August 2026, 5:30 PM – 6:30 PM',
      format: 'Online (Webinar)',
      description: 'A panel discussion on love, companionship, and inclusion for persons with disabilities — moderated by Danish Mahajan (Radio Udaan) with panelists Tinkesh Kaushik, Aishwarya T.V., and Khushi Ganatra 💙 (open to everyone)'
    },
    registration: {
      source: SOURCES.NOBARRIERS_AUG_2026.registration,
      closesAt: '2026-08-22T17:30:00+05:30',
      active: false,
      cap: 500
    },
    feedback: {
      source: SOURCES.NOBARRIERS_AUG_2026.feedback,
      active: false,
      cap: 150
    }
  },
  WIREDFORLOVE_OCT_2026: {
    id: 'wired-for-love-oct-2026',
    name: 'Wired for Love: Love and Relationships on the Neurodiversity Spectrum',
    theme: 'romance',
    info: {
      date: '10th October 2026, 4:00 PM',
      format: 'Online (Zoom)',
      description: 'A webinar on love, dating, and relationships for people on the neurodiversity spectrum 💙 (open to everyone 18+)'
    },
    registration: {
      source: SOURCES.WIREDFORLOVE_OCT_2026.registration,
      closesAt: '2026-10-10T16:00:00+05:30',
      active: false,
      cap: 500
    },
    feedback: {
      source: SOURCES.WIREDFORLOVE_OCT_2026.feedback,
      active: false,
      cap: 150
    }
  },
  WOMENWEBINAR_NOV_2026: {
    id: 'women-webinar-nov-2026',
    name: 'For Women Only: A Webinar on Buddy Up Network',
    theme: 'default',
    info: {
      date: '22nd November 2026, 11:00 AM – 12:00 PM',
      format: 'Online (Zoom)',
      description: 'Hear from women users of Buddy Up Network about finding friends, a special someone or a life partner — plus safety and accessibility features, interest, region and language based groups, and online meetups, with time for your questions 💜 (open to all women 18+)'
    },
    registration: {
      source: SOURCES.WOMENWEBINAR_NOV_2026.registration,
      closesAt: '2026-11-22T11:00:00+05:30',
      active: true,
      cap: 500
    },
    feedback: {
      source: SOURCES.WOMENWEBINAR_NOV_2026.feedback,
      active: false,
      cap: 150
    }
  },
  // New events: add entry here, add sources in eventSources.js, update exports below
};

// ─── Open / closed ───────────────────────────────────────────────────────────
// A missing or invalid closesAt parses to NaN, so the window reads as closed —
// a config mistake can never leave a form open forever.
const isWindowOpen = (window, now) => window.active && now < Date.parse(window.closesAt);

export const isRegistrationOpen = (event, now = Date.now()) => isWindowOpen(event.registration, now);
export const isFeedbackOpen = (event, now = Date.now()) => isWindowOpen(event.feedback, now);

// Every open event accepts signups concurrently — the registration page shows a
// picker when there's more than one. To run 2-3 events at once, set active: true
// and a future closesAt on each; no other config is needed.
export const getOpenRegistrationEvents = (now = Date.now()) =>
  Object.values(EVENTS).filter(e => isRegistrationOpen(e, now));

// Feedback stays single — only one event collects feedback at a time.
export const CURRENT_FEEDBACK_EVENT = EVENTS.WOMENS_MAR_2026;

// ─── Config validation ───────────────────────────────────────────────────────
// Runs as the first step of `npm run build` (src/config/events.test.js) and on
// every `npm start` load, so a missing closesAt is caught before it ships.
const ISO_WITH_OFFSET = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(Z|[+-]\d{2}:\d{2})$/;

const closesAtProblem = (key, kind, closesAt) => {
  if (closesAt === undefined) return `${key}.${kind}.closesAt is missing`;
  if (typeof closesAt !== 'string' || !ISO_WITH_OFFSET.test(closesAt) || Number.isNaN(Date.parse(closesAt))) {
    return `${key}.${kind}.closesAt must look like '2026-10-10T16:00:00+05:30' (got ${JSON.stringify(closesAt)})`;
  }
  return null;
};

export const validateEvents = (events = EVENTS) => Object.entries(events).flatMap(([key, event]) => [
  closesAtProblem(key, 'registration', event.registration.closesAt),
  event.feedback.active || event.feedback.closesAt !== undefined
    ? closesAtProblem(key, 'feedback', event.feedback.closesAt)
    : null
]).filter(Boolean);

if (process.env.NODE_ENV === 'development') {
  const problems = validateEvents();
  if (problems.length) throw new Error(`Invalid event config in src/config/events.js:\n- ${problems.join('\n- ')}`);
}